import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { Button, Input } from '../components/ui'

function AuthShell({ children, title, sub }: { children: React.ReactNode; title: string; sub: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 12px' }}>
            💬
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#F9FAFB', letterSpacing: '-0.4px' }}>FeedbackKit</div>
        </div>

        <div style={{ background: '#141414', border: '1px solid #222', borderRadius: 12, padding: 28 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 6, color: '#F9FAFB' }}>{title}</h1>
          <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>{sub}</p>
          {children}
        </div>
      </div>
    </div>
  )
}

export function Login() {
  const { login } = useAuth()
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setErr('')
    try {
      await login(email, password)
      nav('/dashboard/overview')
    } catch (e: any) {
      setErr(e.message)
    } finally { setLoading(false) }
  }

  return (
    <AuthShell title="Welcome back" sub="Sign in to your account">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
        <Input label="Password" value={password} onChange={setPassword} type="password" placeholder="••••••••" />
        {err && <div style={{ color: '#EF4444', fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
          <Link to="/forgot-password" style={{ color: '#6366F1', textDecoration: 'none', fontSize: 13 }}>Forgot password?</Link>
        </div>
        <Button type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
        <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
          No account?{' '}
          <Link to="/register" style={{ color: '#6366F1', textDecoration: 'none' }}>Create one free</Link>
        </div>
      </form>
    </AuthShell>
  )
}

export function Register() {
  const { register } = useAuth()
  const nav = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return }
    setLoading(true)
    setErr('')
    try {
      await register(name, email, password)
      nav('/dashboard/setup')
    } catch (e: any) {
      setErr(e.message)
    } finally { setLoading(false) }
  }

  return (
    <AuthShell
      title="Create your account"
      sub={<>Setup takes 3 minutes · <span style={{ color: '#34D399' }}>No credit card required</span></>}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Name" value={name} onChange={setName} placeholder="Alex Harrison" />
        <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
        <Input label="Password" value={password} onChange={setPassword} type="password" placeholder="Min 8 characters" />
        {err && <div style={{ color: '#EF4444', fontSize: 13 }}>{err}</div>}
        <Button type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
        <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#6366F1', textDecoration: 'none' }}>Sign in</Link>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, paddingTop: 8, borderTop: '1px solid #1F2937' }}>
          {['🔒 Encrypted', '⚡ 3 min setup', '💳 No card needed'].map(t => (
            <span key={t} style={{ fontSize: 12, color: '#6B7280' }}>{t}</span>
          ))}
        </div>
      </form>
    </AuthShell>
  )
}

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setErr('')
    try {
      await api.auth.forgotPassword(email)
      setSent(true)
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title="Reset your password" sub="We'll send you a link to set a new one">
      {sent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#052e16', border: '1px solid #166534', borderRadius: 8, padding: '12px 16px', color: '#34D399', fontSize: 14 }}>
            If an account exists for <strong>{email}</strong>, a reset link is on its way.
          </div>
          <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
            <Link to="/login" style={{ color: '#6366F1', textDecoration: 'none' }}>Back to sign in</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
          {err && <div style={{ color: '#EF4444', fontSize: 13 }}>{err}</div>}
          <Button type="submit" disabled={loading || !email} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
            {loading ? 'Sending…' : 'Send reset link'}
          </Button>
          <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
            <Link to="/login" style={{ color: '#6366F1', textDecoration: 'none' }}>Back to sign in</Link>
          </div>
        </form>
      )}
    </AuthShell>
  )
}

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const nav = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirm) { setErr('Passwords do not match'); return }
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return }
    setLoading(true)
    setErr('')
    try {
      await api.auth.resetPassword(token, password)
      nav('/login?reset=1')
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invalid link" sub="This reset link is missing or malformed">
        <div style={{ textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
          <Link to="/forgot-password" style={{ color: '#6366F1', textDecoration: 'none' }}>Request a new one</Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Set new password" sub="Choose a strong password for your account">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="New password" value={password} onChange={setPassword} type="password" placeholder="Min 8 characters" />
        <Input label="Confirm password" value={confirm} onChange={setConfirm} type="password" placeholder="Repeat password" />
        {err && <div style={{ color: '#EF4444', fontSize: 13 }}>{err}</div>}
        <Button type="submit" disabled={loading || !password || !confirm} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
          {loading ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthShell>
  )
}
